import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { useTwoFaContext } from '@/context/TwoFaContext'

/**
 * `use2FAEnabled` Hook
 *
 * This custom hook is used for managing the state and operations related to the 2FA (Two-Factor Authentication) feature.
 *
 * @returns {Object} Returns an object containing:
 * - showDisable2Fa: A state variable indicating if the UI for disabling 2FA should be shown.
 * - setShowDisable2Fa: A function to set the `showDisable2Fa` state.
 * - isBtnLoading: A state variable indicating if the button for submitting the 2FA code is loading.
 * - handleSubmit: A function to validate and process the disabling of 2FA.
 * - disableOtp: A state variable holding the OTP entered by the user.
 * - setDisableOtp: A function to set the `disableOtp` state.
 * - errOtp: A state variable holding any error message related to the OTP validation.
 * - t: A translation function provided by the i18next library.
 */
function use2FAEnabled() {
  const [showDisable2Fa, setShowDisable2Fa] = useBoolean(false)
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)
  const { setValue, handleDisable } = useTwoFaContext()

  const { t } = useTranslation()

  const [disableOtp, setDisableOtp] = useState('')
  /*const [showBackup, setShowBackup] = useBoolean(
    value.scratch.length > 0 && value.showBackup
  )*/
  const [errOtp, setErrOtp] = useState('')

  // Function to handle the submission of the OTP
  const handleSubmit = () => {
    // Set the loading state of the submit button to true
    setIsBtnLoading(true)

    // Call the `handleDisable` function from the `TwoFaContext`, providing the OTP entered by the user
    handleDisable(disableOtp, {
      // If the OTP is successfully validated and 2FA is disabled,
      // set `showBackup` to false in the context and hide the 2FA disable UI
      onSuccess: () => {
        setValue({
          showBackup: false,
        })
        setShowDisable2Fa(false)
      },

      // If the OTP validation fails, set `errOtp` to the translated message 'Invalid code'
      onFailed: () => setErrOtp(t('Invalid code')),

      // Finally, regardless of success or failure, set the loading state of the submit button to false
      onFinally: () => setIsBtnLoading(false),
    })
  }

  return {
    showDisable2Fa,
    setShowDisable2Fa,
    isBtnLoading,
    handleSubmit,
    disableOtp,
    setDisableOtp,
    errOtp,
    t,
  }
}

export default use2FAEnabled
